import React from 'react';

import { AutocompleteRenderGroupParams } from '@mui/material';

import routes from 'core/routes';

import InfiniteAutocomplete from 'components/InfiniteAutocomplete';
import { StyledTextField } from 'components/Input/styles';

import { APIRequest, SearchRequest } from 'utils/api/types';
import { RiskControlOption } from 'utils/api/modules/entities/entities.types';
import { useInfiniteSearch } from 'utils/hooks/infiniteSearch';

import { GroupOptions, LibraryLink, LibraryLinkRow, StyledGroupHeader, StyledPaper, VisuallyHidden } from './styles';

export const NO_CATEGORY = 'No category';

type ControlPickerProps = {
  id: string;
  request: (params?: SearchRequest) => APIRequest<RiskControlOption[]>;
  value: RiskControlOption | null;
  onChange: (value: RiskControlOption | null) => void;
  refreshKey?: number;
  errorMessage?: string;
};

const PaperWithLibraryLink = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
  ({ children, ...props }, ref) => (
    <StyledPaper {...props} ref={ref}>
      {children}
      <LibraryLinkRow>
        <LibraryLink
          to={routes.dashboard.risks.controls.toString()}
          target="_blank"
          rel="noopener"
          data-testid="control-picker-library-link"
          onMouseDown={(event) => event.preventDefault()}
        >
          Can't find it? Add it in the Controls library
          <VisuallyHidden> (opens in a new tab)</VisuallyHidden>
        </LibraryLink>
      </LibraryLinkRow>
    </StyledPaper>
  )
);

const renderGroup = ({ key, group, children }: AutocompleteRenderGroupParams): React.ReactNode => (
  <li key={key}>
    <StyledGroupHeader component="div" data-testid="control-picker-group">
      {group}
    </StyledGroupHeader>
    <GroupOptions>{children}</GroupOptions>
  </li>
);

const groupBy = (option: RiskControlOption): string => option.category?.name ?? NO_CATEGORY;
const getOptionLabel = (option: RiskControlOption): string => option.name;
const isOptionEqualToValue = (option: RiskControlOption, current: RiskControlOption): boolean =>
  option.uuid === current.uuid;

const ControlPicker: React.FunctionComponent<ControlPickerProps> = ({
  id,
  request,
  value,
  onChange,
  refreshKey,
  errorMessage,
}) => {
  const { results, loading, loadingMore, hasMore, loadMore, searchQuery, appliedQuery } =
    useInfiniteSearch<RiskControlOption>(request, true, { refreshKey });

  return (
    <InfiniteAutocomplete<RiskControlOption>
      id={id}
      data-testid="control-picker"
      options={results}
      value={value}
      loading={loading}
      hasMore={hasMore}
      loadingMore={loadingMore}
      onLoadMore={loadMore}
      onChange={(_, next) => onChange(next)}
      onInputChange={(_, phrase, reason) => {
        if (reason === 'input' || reason === 'clear') searchQuery(phrase);
      }}
      groupBy={groupBy}
      renderGroup={renderGroup}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      renderOption={({ key, ...props }, option) => (
        <li key={key} {...props} data-testid={`control-picker-option-${option.uuid}`}>
          {option.name}
        </li>
      )}
      noOptionsText={appliedQuery ? `No controls match '${appliedQuery}'` : 'No controls to add'}
      slots={{ paper: PaperWithLibraryLink }}
      renderInput={(params) => (
        <StyledTextField
          {...params}
          error={Boolean(errorMessage)}
          helperText={errorMessage}
          placeholder="Select from the controls library"
          inputProps={{ ...params.inputProps, 'aria-required': true }}
        />
      )}
      size="medium"
      fullWidth
    />
  );
};

export default ControlPicker;
